import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Zap, Timer, Trophy, RotateCcw, BookOpen } from 'lucide-react';

const WordSearchGame = () => {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [time, setTime] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [foundWords, setFoundWords] = useState([]);
  const [grid, setGrid] = useState([]);
  const [words, setWords] = useState([]);

  const wordList = ['REACT', 'GAME', 'CYBER', 'AI', 'PUZZLE', 'LOGIC', 'SPEED', 'MEMORY'];

  const startGame = () => {
    setIsPlaying(true);
    setGameOver(false);
    setScore(0);
    setTime(0);
    setFoundWords([]);
    generateGrid();
  };

  const resetGame = () => {
    setIsPlaying(false);
    setGameOver(false);
    setScore(0);
    setTime(0);
    setFoundWords([]);
    setGrid([]);
    setWords([]);
  };

  const generateGrid = () => {
    const size = 10;
    const newGrid = Array(size).fill().map(() => Array(size).fill(''));
    const newWords = [...wordList];
    
    // Place words in grid
    newWords.forEach(word => {
      let placed = false;
      while (!placed) {
        const direction = Math.floor(Math.random() * 8); // 8 directions
        const x = Math.floor(Math.random() * size);
        const y = Math.floor(Math.random() * size);
        
        if (canPlaceWord(newGrid, word, x, y, direction)) {
          placeWord(newGrid, word, x, y, direction);
          placed = true;
        }
      }
    });
    
    // Fill remaining cells with random letters
    for (let i = 0; i < size; i++) {
      for (let j = 0; j < size; j++) {
        if (newGrid[i][j] === '') {
          newGrid[i][j] = String.fromCharCode(65 + Math.floor(Math.random() * 26));
        }
      }
    }
    
    setGrid(newGrid);
    setWords(newWords);
  };

  const canPlaceWord = (grid, word, x, y, direction) => {
    const size = grid.length;
    const dx = [0, 1, 1, 1, 0, -1, -1, -1];
    const dy = [-1, -1, 0, 1, 1, 1, 0, -1];
    
    for (let i = 0; i < word.length; i++) {
      const newX = x + i * dx[direction];
      const newY = y + i * dy[direction];
      
      if (newX < 0 || newX >= size || newY < 0 || newY >= size) {
        return false;
      }
      
      if (grid[newY][newX] !== '' && grid[newY][newX] !== word[i]) {
        return false;
      }
    }
    return true;
  };

  const placeWord = (grid, word, x, y, direction) => {
    const dx = [0, 1, 1, 1, 0, -1, -1, -1];
    const dy = [-1, -1, 0, 1, 1, 1, 0, -1];
    
    for (let i = 0; i < word.length; i++) {
      const newX = x + i * dx[direction];
      const newY = y + i * dy[direction];
      grid[newY][newX] = word[i];
    }
  };

  const checkWord = (word) => {
    if (foundWords.includes(word)) return;
    
    setFoundWords(prev => [...prev, word]);
    setScore(prev => prev + 100);
    
    if (foundWords.length + 1 === words.length) {
      setGameOver(true);
      setIsPlaying(false);
      if (score + 100 > highScore) {
        setHighScore(score + 100);
      }
    }
  };

  useEffect(() => {
    let interval;
    if (isPlaying && !gameOver) {
      interval = setInterval(() => {
        setTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, gameOver]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Game Header */}
      <div className="cyber-card p-6 mb-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div className="flex items-center justify-center space-x-2">
            <Zap className="h-6 w-6 text-neon-blue" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.score')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-blue">{score}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <Trophy className="h-6 w-6 text-neon-green" />
            <div>
              <p className="text-sm text-gray-400 font-tech">High Score</p>
              <p className="text-2xl font-cyber font-bold text-neon-green">{highScore}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <Timer className="h-6 w-6 text-neon-pink" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.time')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-pink">{formatTime(time)}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <RotateCcw className="h-6 w-6 text-neon-yellow" />
            <button
              onClick={resetGame}
              className="cyber-button text-sm px-4 py-2"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Game Instructions */}
      {!isPlaying && !gameOver && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="cyber-card p-6 mb-8 text-center"
        >
          <h3 className="text-2xl font-cyber font-bold text-white mb-4">
            Word Search Challenge
          </h3>
          <p className="text-gray-300 font-tech mb-6">
            Find all the hidden words in the grid. Words can be in any direction!
          </p>
          <button
            onClick={startGame}
            className="cyber-button text-lg px-8 py-3"
          >
            Start Game
          </button>
        </motion.div>
      )}

      {/* Game Area */}
      {isPlaying && !gameOver && (
        <div className="cyber-card p-6 mb-8">
          <div className="text-center mb-6">
            <h4 className="text-xl font-cyber font-bold text-white mb-4">
              Find these words:
            </h4>
            
            {/* Word List */}
            <div className="flex flex-wrap justify-center gap-2 mb-6">
              {words.map((word, index) => (
                <span
                  key={index}
                  className={`px-3 py-1 rounded font-cyber font-bold ${
                    foundWords.includes(word)
                      ? 'bg-neon-green text-dark-300 line-through'
                      : 'bg-neon-blue/20 text-neon-blue'
                  }`}
                >
                  {word}
                </span>
              ))}
            </div>
            
            {/* Word Grid */}
            <div className="flex justify-center">
              <div className="grid grid-cols-10 gap-1 bg-dark-200 p-4 rounded-lg">
                {grid.map((row, i) =>
                  row.map((cell, j) => (
                    <div
                      key={`${i}-${j}`}
                      className="w-8 h-8 flex items-center justify-center bg-dark-100 text-neon-blue font-cyber font-bold text-sm rounded"
                    >
                      {cell}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Game Over Message */}
      {gameOver && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="cyber-card p-6 mb-8 text-center border-neon-green/50"
        >
          <h3 className="text-3xl font-cyber font-bold text-neon-green mb-4">
            Congratulations! 🎉
          </h3>
          <p className="text-gray-300 font-tech mb-4">
            You found all the words! Score: {score} | Time: {formatTime(time)}
          </p>
          <div className="flex justify-center space-x-4">
            <button
              onClick={startGame}
              className="cyber-button text-lg px-6 py-3"
            >
              Play Again
            </button>
            <button
              onClick={resetGame}
              className="cyber-button text-lg px-6 py-3 border-neon-green text-neon-green hover:bg-neon-green hover:text-dark-300"
            >
              Reset
            </button>
          </div>
        </motion.div>
      )}

      {/* Instructions */}
      <div className="cyber-card p-6">
        <div className="text-center">
          <h4 className="text-xl font-cyber font-bold text-white mb-4">
            How to Play
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-300 font-tech">
            <div>
              <p className="font-bold text-neon-blue mb-2">Objective</p>
              <p>Find all hidden words in the grid</p>
            </div>
            <div>
              <p className="font-bold text-neon-green mb-2">Directions</p>
              <p>Words can be horizontal, vertical, or diagonal</p>
            </div>
            <div>
              <p className="font-bold text-neon-pink mb-2">Scoring</p>
              <p>+100 points per word found</p>
            </div>
            <div>
              <p className="font-bold text-neon-yellow mb-2">Strategy</p>
              <p>Look for patterns and scan systematically!</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WordSearchGame;
